import { createSharedComposable } from "@vueuse/core";
import { Component, computed, ref } from "vue";

export enum FloatingElementPosition {
    TopLeft = 'top-left',
    TopRight = 'top-right',
    TopCenter = 'top-center',
    BottomLeft = 'bottom-left',
    BottomRight = 'bottom-right',
    BottomCenter = 'bottom-center',
};

export type QueueElement<C extends Component = Component> = {
    component: C;
    duration: number;
    position: FloatingElementPosition;
    id: string;
};

const floatingElementsQueueComposable = () => {
    const elements = ref<QueueElement[]>([]);
    const currentElement = computed(() => elements.value[0] || null);
    const currentTimer = ref<number | null>(null);
    
    const queueElement = (element: QueueElement) => {
        elements.value.push(element);
        if (!currentTimer.value) {
            showNextElement();
        }
    };

    const showNextElement = () => {
        if (elements.value.length === 0) {
            return;
        }
        currentTimer.value = window.setTimeout(() => {
            elements.value.shift();
            currentTimer.value = null;
            showNextElement();
        }, currentElement.value.duration);
    };

    const clearQueue = () => {
        if (currentTimer.value) {
            window.clearTimeout(currentTimer.value);
        }
        elements.value = [];
    };

    const clearId = (id: string) => {
        elements.value = elements.value.filter(element => element.id !== id);
    };

    return {
        elements,
        currentElement,
        queueElement,
        clearQueue,
        clearId
    }
};

export const useFloatingElementsQueue = createSharedComposable(floatingElementsQueueComposable);